'use client';

// Inspired by react-hot-toast library
import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/components/ui/toasts/toast';
import { set_timeout } from '@/utils/utils_client';
import { CALLBACK__void } from '@shared/types/types';
import { useState } from 'react';
import { use_effect } from './_base/use_effect';

//todo: move this file to /ui/toasts

const TOAST_LIMIT = 5;
const TOAST_DELETE_DELAY = 3000;

export type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  hide_button_close?: boolean;
  className?: string;
  onDismiss?: CALLBACK__void;
};

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | { type: ActionType['ADD_TOAST']; toast: ToasterToast }
  | { type: ActionType['UPDATE_TOAST']; toast: Partial<ToasterToast> }
  | { type: ActionType['DISMISS_TOAST']; toastId?: ToasterToast['id'] }
  | { type: ActionType['REMOVE_TOAST']; toastId?: ToasterToast['id'] };

interface State {
  toasts: ToasterToast[];
}

//todo: clear timeouts
const toastTimeouts = new Map<string, number>();

function addToRemoveQueue(toastId: string) {
  if (toastTimeouts.has(toastId)) return;

  const timeout = set_timeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: 'REMOVE_TOAST', toastId: toastId });
  }, TOAST_DELETE_DELAY);

  toastTimeouts.set(toastId, timeout);
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_TOAST':
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map(t => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach(toast => addToRemoveQueue(toast.id));
      }

      return {
        ...state,
        toasts: state.toasts.map(t => (t.id === toastId || toastId === undefined ? { ...t, open: false } : t)),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) return { ...state, toasts: [] };

      return { ...state, toasts: state.toasts.filter(t => t.id !== action.toastId) };
  }
}

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);

  listeners.forEach(listener => listener(memoryState));
}

export type Toast = Omit<ToasterToast, 'id'>;

export interface ToastResult {
  id: string;
  dismiss: CALLBACK__void;
  update: (props: ToasterToast) => void;
}

function toast({ onDismiss, ...props }: Toast): ToastResult {
  const id = genId();

  const update = (props: ToasterToast) => dispatch({ type: 'UPDATE_TOAST', toast: { ...props, id } });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: open => {
        if (!open) {
          onDismiss?.();
          dismiss();
        }
      },
    },
  });

  return { id, dismiss, update };
}

export type ToastCallback = typeof toast;

function useToast() {
  const [state, set_state] = useState<State>(memoryState);

  /*
   * EFFECTS
   */

  use_effect(() => {
    listeners.push(set_state);

    return () => {
      const index = listeners.indexOf(set_state);

      if (index > -1) listeners.splice(index, 1);
    };
  }, [state]);

  /*
   * METHODS
   */

  function dismiss(toast_id?: string) {
    dispatch({ type: 'DISMISS_TOAST', toastId: toast_id });
  }

  return { ...state, toast, dismiss };
}

export { toast, useToast };
